/** The spine view's layout (plan §1.1, §5): the registry and one booking → the stage
 *  panels in lifecycle order, then the Communication lane. Pure — no draft state; the
 *  panels' FieldSlots read the draft themselves.
 *
 *  Each section splits into the Editable block (the booking's and its guest's fields,
 *  plus one row per party member for the `party` fields), the "From <restech>" vendor
 *  block and the System block. A catalog key the registry does not place is not shown
 *  (the contract test fails CI first); a registry key the catalog lacks is skipped.
 *  Over a part of the registry (one facet, `tabsLayout`) it lays out just that part:
 *  stages the part does not reach come back with no blocks. */
import {
  BOOKING_RECORD_STAGES,
  catalogActionFields,
  type BookingRecordCatalog,
  type BookingRecordCatalogField,
  type BookingRecordEditContext,
  type BookingRecordStage,
} from '@/api/bookingRecord';
import type { BookingDetail } from '@/api/bookings';
import { actionPath } from '@/features/records/draft/changeSet';
import type {
  FieldBlockKind,
  RecordViewDef,
  ViewBlock,
  ViewFieldRef,
  ViewRow,
  ViewSection,
} from '@/features/records/views/types';

import { STAGE_TITLES, type BookingRegistry } from '../registry';

/** What a booking view lays out. */
export interface BookingRecordData {
  catalog: BookingRecordCatalog;
  editContext: BookingRecordEditContext;
  detail: BookingDetail;
}

/** The DOM id of a stage panel — the anchor the spine nodes and `1`–`5` target. */
export function stageAnchorId(stage: BookingRecordStage): string {
  return `booking-stage-${stage}`;
}

/** Display name of whoever feeds the booking ("Ventrata"); null when unmanaged. */
export function managedByName(ctx: BookingRecordEditContext): string | null {
  const r = ctx.restech;
  return r.managed_by ? (r.managed_by_display ?? r.managed_by) : null;
}

function personName(u: { first_name?: string | null; last_name?: string | null } | null | undefined): string {
  return [u?.first_name, u?.last_name].filter(Boolean).join(' ');
}

const BLOCK_ORDER: FieldBlockKind[] = ['editable', 'vendor', 'system'];

export function spineSections(
  registry: BookingRegistry,
  { catalog, editContext: ctx }: BookingRecordData,
): ViewSection<BookingRecordStage>[] {
  const queued = new Set(Object.keys(catalogActionFields(catalog)));
  const pathOf = (f: BookingRecordCatalogField) =>
    f.kind === 'action' && f.action && queued.has(f.action) ? actionPath(f.action) : f.key;
  const ref = (f: BookingRecordCatalogField, entity: string, id: number): ViewFieldRef => ({
    key: f.key,
    entity,
    id,
    path: pathOf(f),
  });
  const guestId = ctx.guest?.user_id ?? null;
  const placed = catalog.fields
    .filter((f) => registry[f.key] !== undefined)
    .sort((a, b) => registry[a.key]!.order - registry[b.key]!.order);
  const partyFields = placed.filter((f) => f.party);
  const vendorLabel = `From ${managedByName(ctx) ?? 'the booking system'}`;

  return BOOKING_RECORD_STAGES.map((stage) => {
    const blocks: ViewBlock[] = [];
    for (const kind of BLOCK_ORDER) {
      const fields: ViewFieldRef[] = [];
      for (const f of placed) {
        const entry = registry[f.key]!;
        if (entry.stage !== stage || entry.block !== kind) continue;
        if (f.entity === 'user') {
          if (guestId !== null) fields.push(ref(f, 'user', guestId));
        } else {
          fields.push(ref(f, 'booking', ctx.booking_id));
        }
      }
      const rows: ViewRow[] =
        kind === 'editable' && stage === 'precheckin' && partyFields.length > 0
          ? ctx.party.map((m) => ({
              entity: 'booking',
              id: m.booking_id,
              label: personName(m.user) || `Booking #${m.booking_id}`,
              fields: partyFields.flatMap((f) =>
                f.entity === 'user'
                  ? m.user
                    ? [ref(f, 'user', m.user.id)]
                    : []
                  : [ref(f, 'booking', m.booking_id)],
              ),
            }))
          : [];
      if (fields.length === 0 && rows.length === 0) continue;
      blocks.push({
        kind,
        ...(kind === 'vendor' ? { label: vendorLabel } : kind === 'system' ? { label: 'System' } : {}),
        fields,
        ...(rows.length > 0 ? { rows } : {}),
      });
    }
    return { id: stage, title: STAGE_TITLES[stage], blocks };
  });
}

export const SPINE_VIEW: RecordViewDef<BookingRegistry, BookingRecordData, BookingRecordStage> = {
  id: 'spine',
  label: 'Spine',
  layout: spineSections,
};
